import React, { type FC, useCallback } from "react";
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import {
  type SetupIntentResult,
  type Stripe,
  type StripeElements,
  type StripePaymentElementChangeEvent,
  type StripePaymentElementOptions,
} from "@stripe/stripe-js";
import { stripeFormFormTestId, stripeFormId, stripeFormPaymentElementId } from "./const";

export interface StripeFormProps {
  readonly onSubmit: (confirmSetup: () => Promise<SetupIntentResult | undefined>) => void;
  readonly onChange: (event: StripePaymentElementChangeEvent) => void;
}

export const StripeForm: FC<StripeFormProps> = ({ onSubmit, onChange }) => {
  const stripe: Stripe | null = useStripe();
  const elements: StripeElements | null = useElements();

  const paymentElementOptions: StripePaymentElementOptions = {
    fields: {
      billingDetails: {
        address: {
          country: "never",
        },
      },
    },
  };

  const confirmSetup: () => Promise<SetupIntentResult | undefined> = useCallback(async () => {
    if (stripe === null || elements === null) {
      return;
    }

    return await stripe.confirmSetup({
      elements,
      redirect: "if_required",
      confirmParams: {
        payment_method_data: {
          billing_details: {
            address: {
              country: "JP",
            },
          },
        },
      },
    });
  }, [elements, stripe]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();

      onSubmit(confirmSetup);
    },
    [confirmSetup, onSubmit],
  );

  return (
    <form
      id={stripeFormId}
      onSubmit={(e) => {
        handleSubmit(e);
      }}
      data-testid={stripeFormFormTestId}
    >
      <PaymentElement id={stripeFormPaymentElementId} options={paymentElementOptions} onChange={onChange} />
    </form>
  );
};
